import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import {
  AUS_POSTCODE_REGEX,
  NAME_REGEX,
  USA_POSTCODE_REGEX,
} from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { genderOptions } from '@/modules/entity/static/gender-options.ts';
import { titleOptions } from '@/modules/entity/static/titles.ts';
import { JointlyInvestorDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { CalendarInput, CalendarInputProps, Select, TextInputField } from 'ui';
import { CountryCodeEnum } from 'api';

export const validationSchemaJointlyInvestorDetails = yup.object<JointlyInvestorDetails>({
  gender: yup.string().trim(),
  first_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  last_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  title: yup.string().trim().required('This field is required'),
  middle_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .nullable()
    .optional(),
  date_of_birth: yup.string().trim().required('Choose date of birth'),
  suburb: yup.string().trim().required('This field is required'),
  state: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS') {
        return schema.required('This field is required');
      }

      return schema;
    }),
  postcode: yup
    .string()
    .trim()
    .when('country', (country, schema) => {
      if (country[0] === 'AUS') {
        return schema.matches(AUS_POSTCODE_REGEX, 'Invalid postcode format');
      }

      if (country[0] === 'USA') {
        return schema.matches(USA_POSTCODE_REGEX, 'Invalid postcode format');
      }

      return schema;
    })
    .required('This field is required'),
  country: yup.string().trim().required('This field is required'),
  address: yup
    .string()
    .trim()
    .required('This field is required')
    .matches(
      /^((Unit\s*\d+[A-Za-z]?\s*\/\s*)?(\d+\s+[A-Za-z].+))$/,
      'Please enter a valid address (e.g. "Unit 4 / 20 Collins Street")',
    ),
});

const initialValues: JointlyInvestorDetails = {
  address: '',
  title: '',
  first_name: '',
  last_name: '',
  middle_name: '',
  gender: '',
  date_of_birth: '',
  suburb: '',
  state: '',
  postcode: '',
  country: CountryCodeEnum.AUS,
};

export const JointlyInvestorDetailsPage = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const generateInitialValues = (): JointlyInvestorDetails => {
    return {
      ...initialValues,
      ...(data?.entity.individual_entity_details?.jointly || {}),
      country: data?.entity.individual_entity_details?.jointly?.country || data?.entity?.country,
    };
  };

  const formik = useFormik<JointlyInvestorDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaJointlyInvestorDetails,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      if (formik.dirty) {
        await updateEntity({
          ...data?.entity,
          individual_entity_details: {
            ...data?.entity.individual_entity_details,
            jointly: {
              ...data?.entity.individual_entity_details?.jointly,
              ...values,
            },
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    formik.setFieldValue('date_of_birth', date ? date.format('YYYY-MM-DD').toString() : '');
    formik.setFieldError('date_of_birth', '');
  };

  const handleChangeSelect = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };
  return (
    <OnboardingWrapper
      title="Jointly investor details"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col space-y-10 justify-start">
        <div className="w-full flex items-start space-x-2">
          <Select
            isClearable
            label="gender"
            options={genderOptions}
            value={formik.values?.gender}
            onChange={value => handleChangeSelect('gender', value || '')}
            error={formik.errors?.gender}
          />
          <Select
            label="title"
            options={titleOptions}
            isClearable
            value={formik.values.title}
            defaultValue={formik.values.title}
            onChange={value => handleChangeSelect('title', value || '')}
            error={formik.errors?.title}
          />
          <TextInputField label="first name" name={`first_name`} />
        </div>
        <div className="w-full flex items-start space-x-2">
          <TextInputField label="Middle name (optional)" name={`middle_name`} />
          <TextInputField label="Last name" name={`last_name`} />
        </div>
        <CalendarInput
          name="date_of_birth"
          onChange={handleChangeDate}
          label="date of birth"
          defaultValue={
            formik.values.date_of_birth ? dayjs(formik.values.date_of_birth) : undefined
          }
          error={formik.errors?.date_of_birth}
          disabledDate={(date: Dayjs) => date.isAfter() && !date.isSame(dayjs(), 'day')}
        />
        <Select
          name="country"
          label="country"
          options={countryOptions}
          value={formik.values.country}
          onChange={value => handleChangeSelect('country', value)}
          error={formik.errors.country}
          isClearable={true}
        />
        <TextInputField name="address" label="Address: Unit / Street Number/ Street Name" />
        <div className="w-full flex items-start justify-between space-x-6">
          <TextInputField name="suburb" label="suburb" />

          {formik.values.country === 'AUS' && (
            <Select
              name="state"
              label="state"
              options={auStateOptions}
              value={formik.values.state}
              onChange={value => handleChangeSelect('state', value)}
              error={formik.errors.state}
              isClearable={true}
            />
          )}
          <TextInputField name="postcode" label="postcode" />
        </div>
      </div>
    </OnboardingWrapper>
  );
};
